import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';
import 'moment/locale/de';
import {
  useListReservationsQuery,
  useConfirmReservationMutation,
  useCancelReservationMutation,
} from '../../../redux/api/reservationApi';
import { useApiErrorToast } from '../../../hooks/useApiError';
import CreateReservationModal from './CreateReservationModal';
import ReservationPickingReceipt from './ReservationPickingReceipt';

const TABS = [
  { key: 'PENDING', label: 'Offen', icon: 'fa-hourglass-half' },
  { key: 'CONFIRMED', label: 'Bestätigt', icon: 'fa-check' },
  { key: 'FULFILLED', label: 'Abgeholt', icon: 'fa-box-open' },
  { key: 'CANCELLED', label: 'Storniert', icon: 'fa-ban' },
];

const SOURCE_LABEL = { OPERATOR: 'Intern', PUBLIC: 'Online' };

const ReservationCard = ({
  reservation,
  onConfirm,
  onCancel,
  onCheckout,
  onPrint,
  busy,
}) => {
  const { status } = reservation;
  return (
    <div className='card mb-3 text-start'>
      <div className='card-body'>
        <div className='d-flex justify-content-between align-items-start'>
          <div>
            <h5 className='mb-1'>{reservation.customer_name}</h5>
            <div className='small text-muted'>
              {reservation.customer_email && (
                <span className='me-2'>
                  <a href={`mailto:${reservation.customer_email}`}
                     aria-label='E-Mail schicken'
                     >
                    <i className='fas fa-envelope me-1' />
                  </a>
                  {reservation.customer_email}
                </span>
              )}
              {reservation.customer_phone && (
                <span>
                  <i className='fas fa-phone me-1' />
                  {reservation.customer_phone}
                </span>
              )}
            </div>
          </div>
          <span className='badge bg-secondary'>
            {SOURCE_LABEL[reservation.source] || reservation.source}
          </span>
        </div>

        <ul className='list-group list-group-flush my-2'>
          {reservation.items.map((item) => (
            <li
              key={item.id}
              className='list-group-item d-flex justify-content-between px-0 py-1 border-0'
            >
              <span>
                <strong>{item.quantity}×</strong> {item.article_name}
              </span>
              <span className='text-muted small'>
                {Number(item.price || 0).toFixed(2)} €
              </span>
            </li>
          ))}
        </ul>

        {reservation.note && (
          <p className='small fst-italic text-muted mb-1'>
            „{reservation.note}“
          </p>
        )}
        <div className='small text-muted'>
          {reservation.pickup_until && (
            <span className='me-2'>
              <i className='fas fa-calendar-day me-1' />
              Abholung bis{' '}
              {moment(reservation.pickup_until, 'YYYY-MM-DD').format('DD.MM.YYYY')}
            </span>
          )}
          <span>
            <i className='fas fa-clock me-1' />
            {moment(reservation.created_at).format('DD.MM.YYYY HH:mm')}
          </span>
        </div>

        {(status === 'PENDING' || status === 'CONFIRMED') && (
          <div className='d-flex gap-2 mt-3'>
            {status === 'PENDING' && (
              <button
                className='btn btn-success btn-sm'
                disabled={busy}
                onClick={() => onConfirm(reservation)}
              >
                <i className='fas fa-check me-1' />
                Bestätigen
              </button>
            )}
            {status === 'CONFIRMED' && (
              <>
                <button
                  className='btn btn-primary btn-sm'
                  onClick={() => onCheckout(reservation)}
                >
                  <i className='fas fa-cash-register me-1' />
                  Kassieren
                </button>
                <button
                  className='btn btn-outline-secondary btn-sm'
                  onClick={() => onPrint(reservation)}
                >
                  <i className='fas fa-print me-1' />
                  Abholbeleg
                </button>
              </>
            )}
            <button
              className='btn btn-outline-danger btn-sm'
              disabled={busy}
              onClick={() => onCancel(reservation)}
            >
              <i className='fas fa-ban me-1' />
              {status === 'PENDING' ? 'Ablehnen' : 'Stornieren'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const ReservationManagement = () => {
  const [activeTab, setActiveTab] = useState('PENDING');
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState('');
  // Reservation whose picking receipt is currently mounted for printing, plus a
  // signal bumped on each request so re-printing the same reservation re-fires.
  const [printReservation, setPrintReservation] = useState(null);
  const [printSignal, setPrintSignal] = useState(0);
  const navigate = useNavigate();
  const apiErrorToast = useApiErrorToast();

  const { data: reservations, isLoading, isFetching } = useListReservationsQuery({
    status: activeTab,
  });

  const [confirmReservation, { isLoading: confirming }] =
    useConfirmReservationMutation();
  const [cancelReservation, { isLoading: cancelling }] =
    useCancelReservationMutation();
  const busy = confirming || cancelling;

  const handleConfirm = async (reservation) => {
    try {
      await confirmReservation(reservation.id).unwrap();
      toast.success('Reservierung bestätigt');
    } catch (err) {
      apiErrorToast(err, 'Bestätigen fehlgeschlagen');
    }
  };

  const handleCancel = async (reservation) => {
    try {
      await cancelReservation(reservation.id).unwrap();
      toast.success('Reservierung storniert');
    } catch (err) {
      apiErrorToast(err, 'Stornieren fehlgeschlagen');
    }
  };

  const handleCheckout = (reservation) => {
    navigate('/markt/sell', { state: { reservation } });
  };

  // Mount the hidden receipt for this reservation, then fire the print dialog
  // once it has rendered.
  const handlePrint = (reservation) => {
    setPrintReservation(reservation);
    setPrintSignal((n) => n + 1);
  };

  useEffect(() => {
    if (printSignal > 0) {
      const t = setTimeout(() => window.print(), 80);
      return () => clearTimeout(t);
    }
  }, [printSignal]);

  const query = search.trim().toLowerCase();
  const items = (reservations || []).filter((reservation) => {
    if (!query) return true;
    return ['customer_name', 'customer_email', 'note'].some((field) =>
      (reservation[field] || '').toLowerCase().includes(query)
    );
  });

  return (
    <div className='text-start'>
      <div className='d-flex justify-content-between align-items-center mb-3'>
        <h3 className='mb-0'>
          <i className='fas fa-box me-2' />
          Reservierungen
        </h3>
        <button className='btn btn-success' onClick={() => setShowCreate(true)}>
          <i className='fas fa-plus me-2' />
          Neue Reservierung
        </button>
      </div>

      <ul className='nav nav-tabs mb-3'>
        {TABS.map((tab) => (
          <li className='nav-item' key={tab.key}>
            <button
              className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <i className={`fas ${tab.icon} me-1`} />
              {tab.label}
            </button>
          </li>
        ))}
      </ul>

      <div className='input-group mb-3'>
        <span className='input-group-text'>
          <i className='fas fa-search' />
        </span>
        <input
          type='text'
          className='form-control'
          placeholder='Suche nach Name, E-Mail oder Notiz…'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button
          className='btn btn-outline-secondary'
          type='button'
          disabled={!search}
          onClick={() => setSearch('')}
        >
          <i className='fas fa-times me-1' />
          Zurücksetzen
        </button>
      </div>

      {isLoading || isFetching ? (
        <p>Wird geladen…</p>
      ) : items.length === 0 ? (
        <div className='alert alert-light'>Keine Reservierungen in dieser Ansicht.</div>
      ) : (
        items.map((reservation) => (
          <ReservationCard
            key={reservation.id}
            reservation={reservation}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
            onCheckout={handleCheckout}
            onPrint={handlePrint}
            busy={busy}
          />
        ))
      )}

      <CreateReservationModal
        show={showCreate}
        onHide={() => setShowCreate(false)}
      />

      {/* Hidden picking receipt, printed via window.print(). */}
      <ReservationPickingReceipt reservation={printReservation} />
    </div>
  );
};

export default ReservationManagement;
